import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithGoogle, signInWithKakao, handleGoogleOAuthCallback } from '../services/socialAuth';

// 공유 인증 모듈에서 import
import {
  USER_TIERS,
  USER_STATUS,
  getToken,
  removeToken,
  setUserCache,
  getUserCache,
  clearUserCache,
  authApi,
  usersApi
} from '../shared/auth';

// Re-export for backward compatibility
export { USER_TIERS, USER_STATUS };

// ============================================================================
// Context & Hook
// ============================================================================

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ============================================================================
// Provider
// ============================================================================

export const AuthProvider = ({ children }) => {
  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsTier, setViewAsTier] = useState(null);

  // ============================================================================
  // Session Management
  // ============================================================================

  useEffect(() => {
    const restoreSession = async () => {
      // Handle Google OAuth redirect callback
      if (window.location.hash && window.location.hash.includes('access_token')) {
        try {
          console.log('[Auth] Processing Google OAuth callback...');
          const socialUser = await handleGoogleOAuthCallback();
          if (socialUser) {
            const userData = await authApi.socialLogin('google', socialUser);
            setUser(userData);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to process Google OAuth callback:', err);
        }
      }

      // Restore session from token
      const token = getToken();
      if (token) {
        try {
          const userData = await authApi.getMe();
          setUser(userData);
          // 캐시 업데이트
          setUserCache(userData);
        } catch (err) {
          console.error('Failed to restore session:', err);
          // 401 (토큰 만료/무효)인 경우에만 토큰 삭제
          // 네트워크 오류 등은 토큰 유지 (다음 시도에서 복원 가능)
          if (err.status === 401 || err.message?.includes('Invalid token')) {
            console.log('Token invalid or expired, removing...');
            removeToken();
          } else {
            console.log('Network error, keeping token for retry...');
            // 네트워크 오류 시에도 저장된 사용자 정보 복원 시도
            const cachedUser = getUserCache();
            if (cachedUser) {
              setUser(cachedUser);
              console.log('Restored user from cache');
            }
          }
        }
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  // ============================================================================
  // Authentication Functions
  // ============================================================================

  const login = async (email, password) => {
    const userData = await authApi.login(email, password);
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password) => {
    await authApi.signup(name, email, password);
    return { pending: true };
  };

  const socialLogin = async (provider) => {
    let socialUser;

    if (provider === 'google') {
      socialUser = await signInWithGoogle();
    } else if (provider === 'kakao') {
      socialUser = await signInWithKakao();
    } else {
      throw new Error('Unknown provider');
    }

    const userData = await authApi.socialLogin(provider, socialUser);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
  };

  // ============================================================================
  // Access Control
  // ============================================================================

  const hasAccess = (requiredTiers) => {
    if (!user) return false;
    const effectiveTier = viewAsTier || user.tier;
    return requiredTiers.includes(effectiveTier);
  };

  // ============================================================================
  // Admin Functions - User Management
  // ============================================================================

  const verifyAdmin = () => {
    if (!user || user.tier !== USER_TIERS.ADMIN) {
      throw new Error('Admin access required');
    }
  };

  const getAllUsers = async () => {
    verifyAdmin();
    return await usersApi.getAll();
  };

  const getPendingUsers = async () => {
    verifyAdmin();
    return await usersApi.getPending();
  };

  const approveUser = async (userId) => {
    verifyAdmin();
    await usersApi.approve(userId);
  };

  const rejectUser = async (userId) => {
    verifyAdmin();
    await usersApi.reject(userId);
  };

  const updateUserTier = async (userId, newTier) => {
    verifyAdmin();
    await usersApi.updateTier(userId, newTier);
  };

  const deleteUser = async (userId) => {
    verifyAdmin();
    if (userId === user.id) {
      throw new Error('Cannot delete yourself');
    }
    await usersApi.delete(userId);
  };

  // ============================================================================
  // Admin Functions - Tier Simulation (for testing access control)
  // ============================================================================

  const setViewAs = (tier) => {
    if (!user || user.tier !== USER_TIERS.ADMIN) return;
    setViewAsTier(tier);
  };

  const resetViewAs = () => setViewAsTier(null);
  const getCurrentViewTier = () => viewAsTier;
  const getActualTier = () => user?.tier;

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = {
    // State
    user,
    loading,
    viewAsTier,

    // Constants
    USER_TIERS,
    USER_STATUS,

    // Auth Status
    isAuthenticated: !!user,

    // Authentication
    login,
    signup,
    socialLogin,
    logout,

    // Access Control
    hasAccess,

    // Admin - User Management
    getAllUsers,
    getPendingUsers,
    approveUser,
    rejectUser,
    updateUserTier,
    deleteUser,

    // Admin - Tier Simulation
    setViewAs,
    resetViewAs,
    getCurrentViewTier,
    getActualTier,

    // Token
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
